<template>
    <div>
        <main-section-title>{{ i18n("views.DiscountEdit.Applicability") }}:</main-section-title>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.AllApplications") }}</form-label>
            <form-field>
                <SwitchInput v-model="allApplications" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="!allApplications">
                <form-label>{{ i18n("views.DiscountEdit.Applications") }}</form-label>
                <form-field>
                    <EntityMultiSelectBox
                        ref="applicableUseCaseIdsSelect"
                        v-model="applicableUseCaseIds"
                        :dataSource="useCasesDataSource"
                    />
                    <template v-if="DEBUG">
                        <div v-for="useCase in applicableUseCaseIds" :key="useCase">
                            {{ useCase }}
                        </div>
                    </template>
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.AllOffers") }}</form-label>
            <form-field>
                <SwitchInput v-model="allOffers" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="!allOffers">
                <form-label>{{ i18n("views.DiscountEdit.Offers") }}</form-label>
                <form-field>
                    <EntityMultiSelectBox
                        ref="applicableOfferIdsSelect"
                        v-model="applicableOfferIds"
                        :dataSource="offersDataSource"
                    />
                    <template v-if="DEBUG">
                        <div v-for="offer in applicableOfferIds" :key="offer">
                            {{ offer }}
                        </div>
                    </template>
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.AllOfferCategories") }}</form-label>
            <form-field>
                <SwitchInput v-model="allMenuCategories" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="!allMenuCategories">
                <form-label>{{ i18n("views.DiscountEdit.OfferCategories") }}</form-label>
                <form-field>
                    <MultiEntityInput
                        ref="applicableMenuCategoryIdsSelect"
                        v-model="applicableMenuCategoryIds"
                        :dataSource="menuCategoriesDataSource"
                    />
                    <template v-if="DEBUG">
                        <div v-for="menuCategory in applicableMenuCategoryIds" :key="menuCategory">
                            {{ menuCategory }}
                        </div>
                    </template>
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label>{{ i18n("views.DiscountEdit.AllItems") }}</form-label>
            <form-field>
                <SwitchInput v-model="allArticles" />
            </form-field>
        </div>

        <FadeTransition>
            <div class="form-group row" v-if="!allArticles">
                <form-label>{{ i18n("views.DiscountEdit.Items") }}</form-label>
                <form-field>
                    <MultiEntityInput
                        ref="applicableArticleIdsSelect"
                        v-model="applicableArticleIds"
                        :dataSource="articlesDataSource"
                    />
                    <template v-if="DEBUG">
                        <div v-for="article in applicableArticleIds" :key="article">
                            {{ article }}
                        </div>
                    </template>
                </form-field>
            </div>
        </FadeTransition>

        <div class="form-group row">
            <form-label :required="true">{{ i18n("views.DiscountEdit.StartDate") }}</form-label>
            <form-field>
                <DateTimePicker ref="startDateTimePicker" v-model="startDateTime" />
            </form-field>
        </div>

        <div class="form-group row form-group-last">
            <form-label :required="true">{{ i18n("views.DiscountEdit.EndDate") }}</form-label>
            <form-field>
                <DateTimePicker ref="endDateTimePicker" v-model="endDateTime" />
            </form-field>
        </div>
    </div>
</template>
<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import type { IDataSource, IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
    import { DiscountsDao } from "@/data/dao/service/DiscountsDao";
    import type {
        IDiscountApplicability,
        IDiscountApplicabilityToUpdate,
        IDiscountApplicableCollections,
    } from "@/data/model/Discount";
    import type { ILinkableEntity, IMultiLanguageLinkableEntity } from "@/data/model/Shared";
    import { DateTime } from "@/data/util/DateTime";
    import { i18n } from "@/portal/Trans";
    import { translate } from "@/shared/localization/language";
    import { logError } from "@/shared/logging/errors";
    import { type Ref, computed, ref, watch } from "vue";

    import FadeTransition from "@/app/components/transitions/FadeTransition.vue";
    import MultiEntityInput from "@/components/elements/MultiEntityInputComponent.vue";
    import type MultiEntityInputComponent from "@/components/elements/MultiEntityInputComponent.vue";
    import EntityMultiSelectBox from "@/portal/components/discounts/EntityMultiSelectBox.vue";
    import DateTimePicker from "@/portal/components/elements/DateTimePicker.vue";
    import SwitchInput from "@/portal/components/elements/SwitchInput.vue";
    import FormField from "@/portal/components/layout/details/FormField.vue";
    import FormLabel from "@/portal/components/layout/details/FormLabel.vue";
    import MainSectionTitle from "@/portal/components/layout/details/MainSectionTitle.vue";

    // todo: remove
    const DEBUG = ref(false);

    const discountsDao = Factory.getInstance<DiscountsDao>(DiscountsDao);

    const props = defineProps<{
        applicability: IDiscountApplicability | undefined;
    }>();

    const allApplications = ref<boolean>(props.applicability?.allApplications ?? true);
    const allOffers = ref<boolean>(props.applicability?.allOffers ?? true);
    const allMenuCategories = ref<boolean>(props.applicability?.allMenuCategories ?? true);
    const allArticles = ref<boolean>(props.applicability?.allArticles ?? true);

    const applicableOrderUseCaseIds = ref<string[]>(props.applicability?.applicableOrderUseCaseIds ?? []);
    const applicablePayUseCaseIds = ref<string[]>(props.applicability?.applicablePayUseCaseIds ?? []);
    const applicableUseCaseIds = ref<string[]>([...applicableOrderUseCaseIds.value, ...applicablePayUseCaseIds.value]);
    const applicableOfferIds = ref<string[]>(props.applicability?.applicableOfferIds ?? []);
    const applicableMenuCategoryIds = ref<string[]>(props.applicability?.applicableMenuCategoryIds ?? []);
    const applicableArticleIds = ref<string[]>(props.applicability?.applicableArticleIds ?? []);

    // #region dates
    const now = new Date();
    const startDateTime = ref<Date | undefined>(props.applicability?.startDateTime ?? now);
    const endDateTime = ref<Date | undefined>(
        props.applicability?.endDateTime ?? DateTime.getEndOfDayForDate(DateTime.addDays(now, 7))
    );

    const startDateTimePicker = ref<InstanceType<typeof DateTimePicker> | null>(null);
    const endDateTimePicker = ref<InstanceType<typeof DateTimePicker> | null>(null);
    // #endregion

    const applicableUseCaseIdsSelect = ref<InstanceType<typeof EntityMultiSelectBox> | null>(null);
    const applicableOfferIdsSelect = ref<InstanceType<typeof EntityMultiSelectBox> | null>(null);
    const applicableMenuCategoryIdsSelect = ref<InstanceType<typeof MultiEntityInputComponent> | null>(null);
    const applicableArticleIdsSelect = ref<InstanceType<typeof MultiEntityInputComponent> | null>(null);

    // #region basic data source collections
    const useCases = computed(() => {
        if (applicableCollections.value == null) return [];

        const orderUseCases = mapToLinkableEntity(applicableCollections.value.orderUseCases);
        sortByName(orderUseCases);

        const payUseCases = mapToLinkableEntity(applicableCollections.value.payUseCases);
        sortByName(payUseCases);

        return [...orderUseCases, ...payUseCases];
    });

    const offers = computed(() => {
        if (applicableCollections.value == null) return [];

        const mappedOffers = applicableCollections.value.offers;
        sortByName(mappedOffers);
        return mappedOffers;
    });

    const menuCategories = computed(() => {
        if (applicableCollections.value == null) return [];

        const mappedMenuCategories = mapToLinkableEntity(applicableCollections.value.menuCategories);
        sortByName(mappedMenuCategories);
        return mappedMenuCategories;
    });

    const articles = computed(() => {
        if (applicableCollections.value == null) return [];

        const mappedArticles = mapToLinkableEntity(applicableCollections.value.articles);
        sortByName(mappedArticles);
        return mappedArticles;
    });

    const options = computed(() => {
        if (applicableCollections.value == null) return [];

        const mappedOptions = mapToLinkableEntity(applicableCollections.value.options);
        sortByName(mappedOptions);
        return mappedOptions;
    });
    // #endregion

    // #region allowed ids
    const allowedMenuCategoryIds = computed(() => {
        if (applicableCollections.value == null) return [];
        if (allOffers.value) return applicableCollections.value.menuCategories.map((x) => x.id);
        if (applicableOfferIds.value.length == 0) return [];

        const offerMenuCategoriesDictionary = applicableCollections.value.offerMenuCategories;

        const allowedMenuCategoryIds = new Set<string>();
        for (const offerId of applicableOfferIds.value) {
            const offerMenuCategoryIds = offerMenuCategoriesDictionary.get(offerId) ?? [];
            for (const menuCategoryId of offerMenuCategoryIds) {
                allowedMenuCategoryIds.add(menuCategoryId);
            }
        }
        return Array.from(allowedMenuCategoryIds);
    });

    const allowedArticleIds = computed(() => {
        if (applicableCollections.value == null) return [];
        if (allOffers.value && allMenuCategories.value) {
            return applicableCollections.value.articles.map((x) => x.id);
        }
        if (filteredMenuCategories.value.length == 0) return [];

        const menuCategoryArticleIdsDictionary = applicableCollections.value.menuCategoryArticles;

        const allowedArticleIds = new Set<string>();
        for (const menuCategoryId of applicableMenuCategoryIds.value) {
            const articleIds = menuCategoryArticleIdsDictionary.get(menuCategoryId) ?? [];
            for (const articleId of articleIds) {
                allowedArticleIds.add(articleId);
            }
        }
        return Array.from(allowedArticleIds);
    });
    // #endregion

    // #region filtered collections
    const filteredMenuCategories = computed(() => {
        return menuCategories.value.filter((x) => allowedMenuCategoryIds.value.includes(x.id));
    });

    const filteredArticles = computed(() => {
        // get allowed articles
        const allowedArticles: ILinkableEntity[] = articles.value.filter((x) => allowedArticleIds.value.includes(x.id));
        const articleOptionsCollection = applicableCollections.value?.articleOptions ?? new Map<string, string[]>();

        // get all options for allowed articles
        const allOptions: ILinkableEntity[] = [];
        for (const article of allowedArticles) {
            const articleOptionIds = articleOptionsCollection.get(article.id) ?? [];
            const articleOptions = options.value.filter((x) => articleOptionIds.includes(x.id));
            allOptions.push(...articleOptions);
        }

        // combine allowed articles and all options - to get all articles matching the criteria
        const combined = [...allowedArticles, ...allOptions];

        // remove duplicates
        const uniqueById = new Map<string, ILinkableEntity>();
        for (const item of combined) {
            if (!uniqueById.has(item.id)) {
                uniqueById.set(item.id, item);
            }
        }

        // sort by name
        const result = Array.from(uniqueById.values());
        sortByName(result);

        return result;
    });
    // #endregion

    function sortByName(items: ILinkableEntity[]) {
        return items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    function mapToLinkableEntity(items: IMultiLanguageLinkableEntity[]) {
        return items.map((x) => ({
            id: x.id,
            name: translate(x.name),
            appearsIn: x.appearsIn,
        }));
    }

    // #region Data sources
    const applicableCollections = ref<IDiscountApplicableCollections | null>(null);

    const useCasesDataSource: IDataSource<ILinkableEntity> = {
        getData: () => getSelectDataSource(useCases),
    };

    const offersDataSource: IDataSource<ILinkableEntity> = {
        getData: () => getSelectDataSource(offers),
    };

    const menuCategoriesDataSource: IMultiEntityLinkDataSource = {
        search: async (excludedIds, term) => {
            await loadApplicableCollections();

            const filtered = filteredMenuCategories.value.filter(
                (x) => !excludedIds.includes(x.id) && x.name.toLowerCase().includes(term?.toLowerCase() ?? "")
            );
            return filtered;
        },
    };

    const articlesDataSource: IMultiEntityLinkDataSource = {
        search: async (excludedIds, term) => {
            await loadApplicableCollections();

            const filtered = filteredArticles.value.filter(
                (x) => !excludedIds.includes(x.id) && x.name.toLowerCase().includes(term?.toLowerCase() ?? "")
            );
            return filtered;
        },
    };

    async function getSelectDataSource(items: Ref<ILinkableEntity[]>) {
        try {
            await loadApplicableCollections();

            return {
                limit: 0,
                offset: 0,
                hasNext: false,
                hasPrev: false,
                items: items.value,
            };
        } catch (ex) {
            logError(ex);
            throw ex;
        }
    }

    let applicableCollectionsPromise: Promise<void> | null = null;
    function loadApplicableCollections(): Promise<void> {
        if (!applicableCollectionsPromise) {
            applicableCollectionsPromise = getApplicableCollections();
        }
        return applicableCollectionsPromise;
    }

    async function getApplicableCollections() {
        const data = await discountsDao.getApplicableCollections();
        applicableCollections.value = data;
    }
    // #endregion

    function validate() {
        let isValid = true;
        if (!allApplications.value && applicableUseCaseIds.value.length == 0) {
            applicableUseCaseIdsSelect.value?.setError(true);
            isValid = false;
        }
        if (!allOffers.value && applicableOfferIds.value.length == 0) {
            applicableOfferIdsSelect.value?.setError(true);
            isValid = false;
        }
        if (!allMenuCategories.value && applicableMenuCategoryIds.value.length == 0) {
            applicableMenuCategoryIdsSelect.value?.setError(true);
            isValid = false;
        }
        if (!allArticles.value && applicableArticleIds.value.length == 0) {
            applicableArticleIdsSelect.value?.setError(true);
            isValid = false;
        }

        if (!startDateTime.value) {
            startDateTimePicker.value?.setError(true);
            isValid = false;
        }

        if (!endDateTime.value) {
            endDateTimePicker.value?.setError(true);
            isValid = false;
        }
        if (startDateTime.value && endDateTime.value && startDateTime.value >= endDateTime.value) {
            startDateTimePicker.value?.setError(true);
            endDateTimePicker.value?.setError(true);
            isValid = false;
        }

        return isValid;
    }

    function getData(): IDiscountApplicabilityToUpdate | undefined {
        if (!validate()) {
            return undefined;
        }

        return {
            allApplications: allApplications.value,
            allOffers: allOffers.value,
            allMenuCategories: allMenuCategories.value,
            allArticles: allArticles.value,

            applicableOrderUseCaseIds: applicableOrderUseCaseIds.value,
            applicablePayUseCaseIds: applicablePayUseCaseIds.value,
            applicableOfferIds: applicableOfferIds.value,
            applicableMenuCategoryIds: applicableMenuCategoryIds.value,
            applicableArticleIds: applicableArticleIds.value,

            startDateTime: DateTime.convertToUTC(startDateTime.value!).toISOString(),
            endDateTime: DateTime.convertToUTC(endDateTime.value!).toISOString(),
        };
    }

    watch(filteredMenuCategories, () => {
        applicableMenuCategoryIdsSelect.value?.refreshDataSource();
    });

    watch(filteredArticles, () => {
        applicableArticleIdsSelect.value?.refreshDataSource();
    });

    watch(applicableUseCaseIds, () => {
        applicableOrderUseCaseIds.value = applicableUseCaseIds.value.filter((x) =>
            applicableCollections.value?.orderUseCases.some((y) => y.id == x)
        );
        applicablePayUseCaseIds.value = applicableUseCaseIds.value.filter((x) =>
            applicableCollections.value?.payUseCases.some((y) => y.id == x)
        );
    });

    watch(allOffers, (value) => {
        if (!value) {
            applicableOfferIds.value = [];
        }
    });

    watch(allApplications, (value) => {
        if (!value) {
            applicableUseCaseIds.value = [];
            applicableOrderUseCaseIds.value = [];
            applicablePayUseCaseIds.value = [];
        }
    });

    watch(allMenuCategories, (value) => {
        if (!value) {
            applicableMenuCategoryIds.value = [];
        }
    });

    watch(allArticles, (value) => {
        if (!value) {
            applicableArticleIds.value = [];
        }
    });

    defineExpose({
        validate,
        getData,
    });
</script>

<style lang="scss"></style>
